import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import roleService from '../../services/RoleService.js';
import { Settings, Pencil, ArrowLeft } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { formatAuditUser } from '../../utils/userDisplay';

const ViewRole = () => {
  const { t } = useTranslation();
  const { roleId } = useParams();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRole();
  }, [roleId]);

  const fetchRole = async () => {
    try {
      const response = await roleService.getById(roleId);
      setRole(response.data);
    } catch (err) {
      Alert.error('Failed to load role details');
      console.error(err);
      navigate('/roles');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">Loading role details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!role) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <Settings className="w-5 h-5 text-buttonBg" />Role Details
          </h1>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header Section */}
          <div className="bg-primary p-3 px-4">
            <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                  {role.role_name}
                </h2>
              </div>
              {/* Action Buttons */}
              <div className="flex-1 p-0 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
                <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/roles/edit/${roleId}`)}>
                  Edit Role
                </Button>
                <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/roles')}>
                  Back to Roles
                </Button>
              </div>
            </div>
          </div>

          {/* Details Sections */}
          <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
            {/* Role Information */}
            <div>
              <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                Role Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Role ID</label>
                  <p className="text-sm sm:text-base text-secondary font-semibold break-all">{role.id}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Role Name</label>
                  <p className="text-sm sm:text-base text-secondary font-semibold break-words">{role.role_name}</p>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div>
              <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                System Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">{t('common.createdBy')}</label>
                  <p className="text-sm sm:text-base text-secondary font-semibold break-words">
                    {role.created_by || role.created_by_display ? formatAuditUser(role, 'created_by') : 'System'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                  <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(role.created_at)}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated By</label>
                  <p className="text-sm sm:text-base text-secondary font-semibold break-words">
                    {role.updated_by || role.updated_by_display ? formatAuditUser(role, 'updated_by') : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated At</label>
                  <p className="text-sm sm:text-base text-secondary font-semibold break-words">{formatDate(role.updated_at)}</p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                Additional Information
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
                  <div>
                    <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">About Roles</h4>
                    <p className="text-xs sm:text-sm text-gray-700">
                      Roles define the access level and permissions for users in the system. 
                      Each user is assigned a role that determines what features and data they can access.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewRole;
